import Idea from "@/types/Idea";
import Instance from "@/types/Instance";
import IdeaProperty from "@/types/IdeaProperty";
import Plain from "@/types/Plain";
import {asArray} from "@/utils/arrayUtils";
import {deepCopy} from "@/utils/copy";
import {InstanceContext} from "@/types/InstanceContext";
import {EnhancedWorkpace} from "@/types/Workspace";

/**
 * O que a ficha de um item mostra ANTES de a leitura completa chegar — e o que ela recebe pronto.
 *
 * A ficha lê o item com 2 níveis de relacionados, os candidatos e as falhas: a leitura mais cara da
 * tela, e a página ficava em branco até ela voltar. Mas quem abre a ficha — a grade, o Kanban, a tabela
 * de um relacionamento — ou quem volta da edição e da criação já tem o item na memória. Esta peça é a
 * dona de "o que dá para mostrar já", num lugar só, para a ficha ter UM caminho de carga.
 *
 * `ViewRead` é a forma da resposta do `get` (e do `InstanceView.asRead`): entregue sozinha, é a leitura
 * COMPLETA — o Expandir da ficha em modal — e a ficha não relê. Com `pending` é uma PRÉVIA: a ficha a
 * mostra e relê por trás. `pending` diz que relacionamentos vieram só resumidos (id, identificador,
 * situação), e a ficha reserva o lugar deles até a leitura completa.
 */
export type ViewRead = { value: Instance, relDomains?: Plain, reverseInfo?: Plain }
export type Preview = ViewRead & { pending: string[] }

export const isPreview = (read: ViewRead): read is Preview => 'pending' in read

/**
 * A leitura da ficha, e de quem a entrega — a edição lida direto do servidor usa a MESMA: a prévia de
 * volta da edição é o que ela leu, e só serve à ficha se tiver a mesma forma (relacionados completos,
 * carimbos).
 *
 * Os carimbos não são para esta tela: com `relProperties` eles descem para dentro do `_rels`, e é de lá
 * que o quadro embutido tira o prazo das raias terminais. Não atrapalham a edição — o `instanceDiff` só
 * percorre as propriedades da coleção, e o servidor recusaria um `_createdAt` num patch. `failures`: o
 * que o item, como está gravado, viola — a ficha o marca em cada campo. `createdBy`/`updatedBy`: quem
 * criou e quem alterou, que a ficha mostra com a cara e o nome. `instance`: o número do item, que o
 * `{{_id}}` do markdown mostra — sem ele, o campo saía vazio na ficha e na edição (relato do usuário); a
 * lista e o quadro já o pediam.
 *
 * `depth` 2 porque a ficha mostra os relacionamentos DOS relacionados: as colunas da tabela de um
 * relacionamento e as raias do quadro embutido. Com 1, o servidor resolve do segundo nível em diante só
 * o que compõe o identificador, e o valor chega como id cru.
 */
export const VIEW_READ = {
    meta: ["rels", "identifier", "instance", "state", "stateChangedAt", "createdAt", "updatedAt", "createdBy", "updatedBy", "failures"],
    options: ["relProperties", "rel-domains"],
    depth: 2
}

const relationshipsOf = (idea: Idea): IdeaProperty[] =>
    idea.properties.filter(property => property.relationship)

/** Os ids de um valor de relacionamento: o id cru, ou o relacionado resolvido. */
function idsOf(value: unknown): number[] {
    return asArray(value as any)
        .map((one: any) => one !== null && typeof one === 'object' ? one._instance : one)
        .filter((id: unknown): id is number => typeof id === 'number')
}

const sameIds = (a: number[], b: number[]) =>
    a.length === b.length && [...a].sort().join() === [...b].sort().join()

/** Vazio é vazio, venha como ausente, nulo ou texto vazio — o rascunho e o gravado divergem nisso. */
const filled = (value: unknown) => value === undefined || value === null || value === '' ? null : value
const sameValue = (a: unknown, b: unknown) => JSON.stringify(filled(a)) === JSON.stringify(filled(b))

/** Os relacionados de um relacionamento, na forma do `_rels`, remontados dos candidatos por id. */
function relatedFrom(property: IdeaProperty, ids: number[], domains?: Plain): Plain | Plain[] | undefined {
    const candidates = asArray((domains || {})[property.relationship!.idea] as Plain[])
    const found = ids.map(id => candidates.find(candidate => candidate._instance === id))
        .filter((candidate): candidate is Plain => !!candidate)
    return property.multiple ? found : found[0]
}

/** Os candidatos e os reversos da coleção, como a leitura de uma lista os traz ao lado dos itens. */
export type RelatedRead = Pick<ViewRead, 'relDomains' | 'reverseInfo'>

/**
 * De um item de grade, de Kanban ou de tabela de relacionamento. Uma cópia: a ficha mexe no item (a
 * situação inicial, a troca de situação), e a lista não pode mudar por baixo. Os relacionados dele
 * vêm resumidos — todos os relacionamentos ficam pendentes.
 *
 * Com os candidatos e os reversos, quando quem abre os tem (`related`): são o que a edição precisa além
 * do item, e com eles ela abre sem ir ao servidor — ver `editingFrom`.
 */
export function previewFromRow(item: Instance, idea: Idea, related: RelatedRead | null = null): Preview {
    return {value: deepCopy(item), ...(related || {}), pending: relationshipsOf(idea).map(property => property.alias)}
}

/**
 * A prévia do item que uma lista acabou de abrir, se for um dos que ela tem: a grade e o Kanban abrem
 * também o item de OUTRA coleção, pelo chip de um relacionamento, e esse não está na lista — sem prévia.
 */
export function previewOfOpened(
    context: { idea: string, instance: number | string }, items: Instance[], idea: Idea,
    related: RelatedRead | null = null
): Preview | null {
    if (context.idea !== idea.alias) return null
    const item = items.find(candidate => candidate._instance === Number(context.instance))
    return item ? previewFromRow(item, idea, related) : null
}

/**
 * O que a edição de um item recebe, montado de uma leitura dele: a coleção, uma cópia editável, os
 * candidatos dos relacionamentos e os reversos. Um dono para os dois que a abrem: a ficha, com o que
 * leu, e a janela aberta JÁ editando (o lápis do card, o "Editar" da janela de recusa), com o que o
 * quadro tinha — que antes ia ao servidor para buscar o que o quadro já tinha lido (relato do usuário).
 *
 * Null sem os candidatos: a edição precisa deles, e então quem abre lê.
 */
export function editingFrom(context: InstanceContext, read: ViewRead, workspace: EnhancedWorkpace): any {
    if (!read.relDomains) return null
    return {
        context,
        idea: workspace.ideaWith(context.idea),
        instance: deepCopy(read.value),
        domains: read.relDomains,
        choiceDomains: workspace.domains,
        reverseInfo: read.reverseInfo
    }
}

/**
 * De volta da edição: o item como ela o recebeu, antes de o formulário mexer — entregue pela ficha, ou
 * lido com a mesma leitura (`VIEW_READ`). Completo no que a ficha mostra, mas ainda uma prévia: o que
 * outro mudou nesse meio-tempo só a leitura traz.
 */
export function previewFromSnapshot(snapshot: ViewRead): Preview {
    return {...snapshot, pending: []}
}

/**
 * De salvar a edição: o original com o que o rascunho trocou. O relacionamento que não mudou mantém o
 * `_rels` completo; o que mudou é remontado dos candidatos (id e identificador) e fica pendente.
 *
 * `undefined` quando um campo do identificador mudou: o título sairia errado, e só o servidor o monta.
 * A ficha carrega como sempre. Calculadas e carimbos ficam os do original — velhos por um instante,
 * até a leitura completa (que é por isso que a ficha só liga rodapé, falhas e ações depois dela).
 */
export function previewFromSaved(
    snapshot: ViewRead, saved: Plain, idea: Idea, domains?: Plain
): Preview | undefined {
    const original = snapshot.value
    const identifierChanged = idea.properties.some(property => property.identifier && (property.relationship
        ? !sameIds(idsOf(original[property.alias] ?? original._rels[property.alias]), idsOf(saved[property.alias]))
        : !sameValue(original[property.alias], saved[property.alias])))
    if (identifierChanged) return undefined

    const value: Plain = {...original}
    const rels: Plain = {...original._rels}
    const pending: string[] = []
    for (const property of idea.properties) {
        if (!property.relationship) {
            if (filled(saved[property.alias]) === null) delete value[property.alias]
            else value[property.alias] = saved[property.alias]
            continue
        }
        const now = idsOf(saved[property.alias])
        if (saved[property.alias] === undefined || sameIds(now, idsOf(rels[property.alias]))) continue
        value[property.alias] = saved[property.alias]
        const related = relatedFrom(property, now, domains)
        if (related === undefined) delete rels[property.alias]
        else rels[property.alias] = related
        pending.push(property.alias)
    }
    if (saved._state !== undefined) value._state = saved._state
    //O que o item violava antes de salvar não vale mais — e o de agora só o servidor sabe
    delete value._failures
    return {value: {...value, _rels: rels} as Instance, relDomains: snapshot.relDomains, reverseInfo: snapshot.reverseInfo, pending}
}

/**
 * De salvar a criação: o rascunho, com o id e o identificador que o servidor deu (`added`) e os
 * relacionados remontados dos candidatos. Sem carimbos nem acesso — nada do que o servidor carimba —,
 * e todos os relacionamentos pendentes: os reversos, em particular, são criados depois do item.
 */
export function previewFromCreated(
    added: Plain, draft: Plain, idea: Idea, domains?: Plain, reverseInfo?: Plain
): Preview {
    const value: Plain = {_instance: added._instance, _identifier: added._identifier}
    const rels: Plain = {}
    for (const property of idea.properties) {
        if (filled(draft[property.alias]) === null) continue
        value[property.alias] = draft[property.alias]
        if (!property.relationship) continue
        const related = relatedFrom(property, idsOf(draft[property.alias]), domains)
        if (asArray(related as any).length) rels[property.alias] = related
    }
    if (draft._state !== undefined) value._state = draft._state
    return {value: {...value, _rels: rels} as Instance, relDomains: domains, reverseInfo,
        pending: relationshipsOf(idea).map(property => property.alias)}
}
